// frontend/src/components/tasks/TaskNotifyDialog.jsx
//
// Envío manual de la notificación de una tarea.
//
// Antes de enviar se PREGUNTA el canal (correo, WhatsApp o ambos) y se muestra
// el mensaje real que se va a mandar. El texto lo arma el backend, así que
// correo y WhatsApp dicen exactamente lo mismo.

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { api } from '../../services/api';

const TaskNotifyDialog = ({ open, taskId, kind = 'assigned', onClose }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [template, setTemplate] = useState(null);
  const [useEmail, setUseEmail] = useState(true);
  const [useWhatsApp, setUseWhatsApp] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (!open || !taskId) return;
    setLoading(true);
    api.tasks.messageTemplate(taskId, kind)
      .then((res) => {
        setTemplate(res.data);
        setEmail(res.data?.assignee_email || '');
        setPhone(res.data?.assignee_phone || '');
      })
      .catch(() => setTemplate(null))
      .finally(() => setLoading(false));
  }, [open, taskId, kind]);

  const send = async () => {
    const channels = [useEmail && 'email', useWhatsApp && 'whatsapp'].filter(Boolean);
    if (!channels.length) {
      toast.error('Elige al menos un canal');
      return;
    }
    setSending(true);
    try {
      const res = await api.tasks.notify(taskId, {
        kind,
        channels,
        comment: comment.trim() || undefined,
        recipient_email: email.trim() || undefined,
        recipient_phone: phone.trim() || undefined,
      });
      const result = res.data?.channels || {};

      if (result.email?.sent) toast.success(`Correo enviado a ${result.email.to}`);
      else if (result.email) toast.error(`Correo: ${result.email.error}`);

      if (result.whatsapp?.sent && result.whatsapp.link) {
        // WhatsApp se abre en el navegador: funciona sin integración directa.
        window.open(result.whatsapp.link, '_blank', 'noopener');
      } else if (result.whatsapp) {
        toast.error(`WhatsApp: ${result.whatsapp.error}`);
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo enviar la notificación');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {kind === 'updated' ? 'Notificar actualización de la tarea' : 'Notificar asignación de la tarea'}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : !template ? (
          <Alert severity="error">No se pudo preparar el mensaje.</Alert>
        ) : (
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="text.secondary">¿Por dónde se envía?</Typography>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={<Checkbox checked={useEmail} onChange={(e) => setUseEmail(e.target.checked)} />}
                  label={(
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <EmailIcon fontSize="small" /><span>Correo</span>
                    </Stack>
                  )}
                />
                <FormControlLabel
                  control={<Checkbox checked={useWhatsApp} onChange={(e) => setUseWhatsApp(e.target.checked)} />}
                  label={(
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <WhatsAppIcon fontSize="small" /><span>WhatsApp</span>
                    </Stack>
                  )}
                />
              </Stack>
            </Box>

            {useEmail && (
              <TextField
                label="Correo de destino" size="small" fullWidth value={email}
                onChange={(e) => setEmail(e.target.value)}
                helperText={email ? ' ' : 'El responsable no tiene correo registrado'}
              />
            )}
            {useWhatsApp && (
              <TextField
                label="Teléfono (con código de país)" size="small" fullWidth value={phone}
                onChange={(e) => setPhone(e.target.value)}
                helperText={phone ? ' ' : 'El responsable no tiene teléfono registrado'}
              />
            )}

            <TextField
              label="Comentario (opcional)" size="small" fullWidth multiline minRows={2}
              value={comment} onChange={(e) => setComment(e.target.value)}
            />

            <Box>
              <Typography variant="overline" color="text.secondary">Mensaje</Typography>
              <Box sx={{
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                whiteSpace: 'pre-line',
                fontSize: '0.82rem',
                maxHeight: 200,
                overflowY: 'auto',
              }}
              >
                {template.text}
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={send} disabled={sending || loading || !template}>
          {sending ? 'Enviando…' : 'Enviar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

TaskNotifyDialog.propTypes = {
  open: PropTypes.bool,
  taskId: PropTypes.string,
  kind: PropTypes.oneOf(['assigned', 'updated']),
  onClose: PropTypes.func.isRequired,
};

export default TaskNotifyDialog;
